/**
 * What: Reports whether an element has scrolled into the viewport at least once, so a caller can
 * start an entrance animation only when it will actually be seen, instead of the instant the page
 * mounts (invisible for anything below the fold).
 * Transport / data-flow: Pure DOM observation via IntersectionObserver; no network, no store.
 *
 */
import { useLayoutEffect, useRef, useState } from 'react'

/**
 * Ref to attach to the animated element, plus whether it has been seen on screen yet. `T` is
 * typically a DOM element type, but also accepts the Shopify web-component prop types (e.g.
 * `HTMLElementTagNameMap['s-button']`), which don't structurally extend `Element` even though
 * the underlying node always is one — hence the internal cast to `Element` for the observer.
 */
export function useAnimateWhenVisible<T>() {
  const ref = useRef<T | null>(null)
  const [hasBeenVisible, setHasBeenVisible] = useState(false)

  useLayoutEffect(() => {
    if (hasBeenVisible) return
    const node = ref.current as Element | null
    if (!node || typeof IntersectionObserver === 'undefined' || typeof window === 'undefined') {
      // No observer support (or no node yet): fall back to showing the animation immediately
      // rather than leaving the element permanently collapsed.
      setHasBeenVisible(true)
      return
    }

    // Checked synchronously, before the browser paints: an element already on screen the moment
    // it mounts has nothing to "reveal" — the user never scrolled to find it — so skip the
    // animation outright rather than firing it in the first frame (effectively invisible, but
    // not free: it was the thing making the entrance feel like it wasn't playing at all).
    const rect = node.getBoundingClientRect()
    const alreadyOnScreen =
      rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth
    if (alreadyOnScreen) {
      setHasBeenVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasBeenVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasBeenVisible])

  return { ref, hasBeenVisible }
}
